import React from 'react';
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Destinations', href: '/destinations' },
    { name: 'Services', href: '/services' },
    { name: 'Why Choose Us', href: '/why-us' },
    { name: 'Contact', href: '/contact' }
  ];

  const destinations = [
    { name: 'Dubai', href: '/destinations/dubai' },
    { name: 'Thailand', href: '/destinations/thailand' },
    { name: 'Maldives', href: '/destinations/maldives' },
    { name: 'Bali', href: '/destinations/bali' },
    { name: 'Vietnam', href: '/destinations/vietnam' },
    { name: 'Sri Lanka', href: '/destinations/sri-lanka' },
    { name: 'Nepal', href: '/destinations/nepal' },
    { name: 'United Kingdom', href: '/destinations/united-kingdom' }
  ];

  const services = [
    { name: 'Custom Itineraries', href: '/services#custom' },
    { name: 'Visa Assistance', href: '/services#visa' },
    { name: 'Luxury Accommodations', href: '/services#hotels' },
    { name: 'Group Travel', href: '/services#group' },
    { name: 'B2B Solutions', href: '/services#b2b' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter section */}
      <div className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Stay Inspired
            </h3>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">
              Get travel tips, destination insights, and exclusive offers delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-6 py-3 rounded-l-full sm:rounded-r-none rounded-r-full bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <button className="mt-3 sm:mt-0 px-8 py-3 bg-white text-[#6C30ED] font-semibold rounded-r-full sm:rounded-l-none rounded-l-full hover:bg-gray-100 transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Company info */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_715bdeb6-1ac6-412a-a604-4e6390e978d0/artifacts/kihnkqca_file.svg" 
                  alt="TRIPTO" 
                  className="h-12 w-auto filter brightness-0 invert"
                />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                TRIPTO is your gateway to extraordinary travel experiences. We specialize in creating 
                personalized journeys that go beyond the ordinary, connecting you with the authentic 
                heart of each destination.
              </p>
              <div className="space-y-3">
                <div className="flex items-center text-gray-300">
                  <MapPin size={18} className="mr-3 text-[#00DF98]" />
                  <span>123 Travel Street, Tourism District, City 12345</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <Phone size={18} className="mr-3 text-[#00DF98]" />
                  <span>+91 7400488233</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <Mail size={18} className="mr-3 text-[#00DF98]" />
                  <span>sales@triptotravels.org</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-[#00DF98] transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Destinations */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Destinations</h4>
              <ul className="space-y-3">
                {destinations.map((dest) => (
                  <li key={dest.name}>
                    <a
                      href={dest.href}
                      className="text-gray-300 hover:text-[#00DF98] transition-colors duration-200"
                    >
                      {dest.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Services</h4>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <a
                      href={service.href}
                      className="text-gray-300 hover:text-[#00DF98] transition-colors duration-200"
                    >
                      {service.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom footer */}
      <div className="border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Social media */}
            <div className="flex space-x-6 mb-4 md:mb-0">
              <a
                href="#"
                className="text-gray-400 hover:text-[#00DF98] transition-colors duration-200"
              >
                <Facebook size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#00DF98] transition-colors duration-200"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#00DF98] transition-colors duration-200"
              >
                <Twitter size={20} />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#00DF98] transition-colors duration-200"
              >
                <Linkedin size={20} />
              </a>
            </div>

            {/* Copyright */}
            <div className="flex flex-col md:flex-row items-center text-gray-400 text-sm">
              <span className="mb-2 md:mb-0 md:mr-6">
                © 2024 TRIPTO. All rights reserved.
              </span>
              <div className="flex space-x-4">
                <a href="/privacy" className="hover:text-[#00DF98] transition-colors">
                  Privacy Policy
                </a>
                <a href="/terms" className="hover:text-[#00DF98] transition-colors">
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;